import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import Button from '@/components/ui/Button';
import { SupportRequestDtoIn } from '@/models/SupportRequestDtoIn';
import SupportRequestType from '@/models/common/SupportRequestType';
import { usePutAuthApi } from '@/hooks/UseApiCalls';
import useFundContext from '@/contexts/Fund/useFundContext';
import { formatDateLocale } from '@/helpers/dateHelper';
import { AUTO_OFFICE_COMMENTS } from '@/constants/officeComments';
import styles from './CancelSupportRequestDialog.module.css';

interface UpdateUserSupportRequestStatusBody {
  correlationId: string;
  counter: number | null;
  newStatus: string;
  applyToOthers: boolean;
  comment?: string;
  officeComment?: string;
}

interface CancelSupportRequestDialogProps {
  open: boolean;
  onClose: () => void;
  request: SupportRequestDtoIn | null;
  onSuccess: () => void;
}

/**
 * Whether the request has at least one month a representative may still cancel.
 *
 * HAT-1345: this file used to carry two hard-coded sets of status names and answer the question
 * itself. They stopped short of PendingExecution - where an approved request waits for the office
 * to pay it - while the server counts that request as one that blocks deleting the beneficiary's
 * bank account, and tells the coordinator to cancel it first. The answer comes from the server
 * now (SupportRequestDtoOut.CanCancel), so the two cannot drift apart again.
 */
export const hasCancellableMonth = (request: SupportRequestDtoIn): boolean => request.canCancel.some(Boolean);

const CancelSupportRequestDialog = ({ open, onClose, request, onSuccess }: CancelSupportRequestDialogProps) => {
  const { t } = useTranslation();
  const { currentFund } = useFundContext();

  const isMonthly = request?.type === SupportRequestType.Monthly;
  const isLifecycle1 = request?.isInInitialStatusProcess ?? false;
  const isMonthlyL2 = isMonthly && !isLifecycle1;

  const [selectedCounter, setSelectedCounter] = useState<number | null>(null);
  const [applyToOthers, setApplyToOthers] = useState(false);
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSucceeded, setIsSucceeded] = useState(false);

  const { mutate: updateStatus, isPending: isSubmitting } = usePutAuthApi<void, object, UpdateUserSupportRequestStatusBody>(
    `funds/${currentFund?.id}/support-requests/cancel`
  );

  useEffect(() => {
    if (open && request) {
      setError(null);
      setApplyToOthers(false);
      setIsSucceeded(false);
      setComment('');

      if (isMonthlyL2) {
        const firstCancellableIdx = request.canCancel.findIndex(Boolean);
        setSelectedCounter(firstCancellableIdx >= 0 ? firstCancellableIdx + 1 : null);
      } else {
        setSelectedCounter(null);
      }
    }
  }, [open, request]);

  const handleSubmit = () => {
    if (!request || !currentFund?.id) return;
    setError(null);

    let counter: number | null;
    let applyToOthersValue: boolean;

    if (isMonthlyL2) {
      counter = selectedCounter;
      applyToOthersValue = applyToOthers;
    } else {
      counter = null;
      applyToOthersValue = false;
    }

    let formattedComment: string | undefined;
    const trimmed = comment.trim();
    if (trimmed) {
      if (isMonthly && counter != null) {
        const monthDate = formatDateLocale(request.requestedForDates[counter - 1]);
        formattedComment = `${t('cancelSupportRequest.commentPrefix.month', { month: monthDate })}: ${trimmed}`;
      } else {
        formattedComment = `${t('cancelSupportRequest.commentPrefix.general')}: ${trimmed}`;
      }
    }

    const body: UpdateUserSupportRequestStatusBody = {
      correlationId: request.correlationId,
      counter,
      newStatus: 'Cancelled',
      applyToOthers: applyToOthersValue,
      comment: formattedComment,
      officeComment: AUTO_OFFICE_COMMENTS.CANCELLED_BY_REPRESENTATIVE,
    };

    updateStatus(
      { body },
      {
        onSuccess: () => {
          setIsSucceeded(true);
          onSuccess();
        },
        onError: (err) => {
          setError(t('cancelSupportRequest.error') + ': ' + err.message);
        },
      }
    );
  };

  const handleClose = () => {
    setError(null);
    setIsSucceeded(false);
    onClose();
  };

  if (!request) return null;

  const isSubmitDisabled = isSubmitting || !comment.trim() || (isMonthlyL2 && selectedCounter === null);

  const renderMonthSelector = (dates: string[], canCancel: boolean[]) => (
    <div className={styles.section}>
      <label className={styles.sectionLabel}>{t('cancelSupportRequest.selectMonth')}:</label>
      <div className={styles.monthList}>
        {dates.map((date, idx) => {
          const counter = idx + 1;
          const cancellable = canCancel[idx] === true;
          return (
            <label key={counter} className={`${styles.radioOption} ${!cancellable ? styles.disabledOption : ''}`}>
              <input
                type="radio"
                name="month"
                value={counter}
                checked={selectedCounter === counter}
                onChange={() => setSelectedCounter(counter)}
                disabled={!cancellable}
              />
              <span>{formatDateLocale(date)}</span>
            </label>
          );
        })}
      </div>
    </div>
  );

  const renderScopeSelector = () => (
    <div className={styles.section}>
      <div className={styles.radioGroup}>
        <label className={styles.radioOption}>
          <input type="radio" name="scope" checked={!applyToOthers} onChange={() => setApplyToOthers(false)} />
          <span>{t('cancelSupportRequest.thisMonthOnly')}</span>
        </label>
        <label className={styles.radioOption}>
          <input type="radio" name="scope" checked={applyToOthers} onChange={() => setApplyToOthers(true)} />
          <span>{t('cancelSupportRequest.fromThisMonthOnward')}</span>
        </label>
      </div>
    </div>
  );

  return (
    <MainDialogLayout
      open={open}
      onOpenChange={(isOpen: boolean) => {
        if (!isOpen) handleClose();
      }}
      title={t('cancelSupportRequest.title')}
    >
      <div className={styles.content}>
        {isSucceeded ? (
          <div className={styles.successContent}>
            <p className={styles.successMessage}>{t('cancelSupportRequest.successMessage')}</p>
            <div className={styles.actions}>
              <Button variant="primary" className={styles.actionButton} onClick={handleClose}>
                {t('cancelSupportRequest.close')}
              </Button>
            </div>
          </div>
        ) : isMonthlyL2 ? (
          <>
            {renderMonthSelector(request.requestedForDates, request.canCancel)}
            {renderScopeSelector()}
          </>
        ) : (
          <p className={styles.confirmText}>{isLifecycle1 ? t('cancelSupportRequest.confirmLifecycle1') : t('cancelSupportRequest.confirmOneTime')}</p>
        )}

        {!isSucceeded && (
          <>
            <div className={styles.section}>
              <label className={styles.sectionLabel}>{t('cancelSupportRequest.commentLabel')}:</label>
              <textarea
                className={styles.commentTextarea}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={t('cancelSupportRequest.commentPlaceholder')}
                rows={3}
              />
            </div>
            {error && <p className={styles.error}>{error}</p>}
            <div className={styles.actions}>
              <Button variant="white" className={styles.secondaryButton} onClick={handleClose} disabled={isSubmitting}>
                {t('cancelSupportRequest.cancel')}
              </Button>
              <Button variant="primary" className={styles.actionButton} onClick={handleSubmit} disabled={isSubmitDisabled}>
                {isSubmitting ? t('loading') : t('cancelSupportRequest.confirm')}
              </Button>
            </div>
          </>
        )}
      </div>
    </MainDialogLayout>
  );
};

export default CancelSupportRequestDialog;
